import { useState, useEffect, useCallback } from 'react'
import { ThreeDots } from 'react-loader-spinner'
import Lightbox from '../components/Lightbox'
import MonthSidebar from '../components/MonthSidebar'
import ImageSEO from '../components/ImageSEO'
import SEO from '../components/SEO'
import './GospelToonsPage.css'

const GOOGLE_DRIVE_API_URL = 'https://script.google.com/macros/s/AKfycbwQfFwCqJoTP0xBdBFpPAiDuFg_GRSWAycC7hW10sa_5Z5WrXWmMsc5ZNXjnjNUA6rieQ/exec'

const months = [
  { key: 'jan', name: 'January', tamil: 'ஜனவரி', days: 31 },
  { key: 'feb', name: 'February', tamil: 'பிப்ரவரி', days: 28 },
  { key: 'mar', name: 'March', tamil: 'மார்ச்', days: 31 },
  { key: 'apr', name: 'April', tamil: 'ஏப்ரல்', days: 30 },
  { key: 'may', name: 'May', tamil: 'மே', days: 31 },
  { key: 'jun', name: 'June', tamil: 'ஜூன்', days: 30 },
  { key: 'jul', name: 'July', tamil: 'ஜூலை', days: 31 },
  { key: 'aug', name: 'August', tamil: 'ஆகஸ்ட்', days: 31 },
  { key: 'sep', name: 'September', tamil: 'செப்டம்பர்', days: 30 },
  { key: 'oct', name: 'October', tamil: 'அக்டோபர்', days: 31 },
  { key: 'nov', name: 'November', tamil: 'நவம்பர்', days: 30 },
  { key: 'dec', name: 'December', tamil: 'டிசம்பர்', days: 31 }
]

const getCurrentMonthKey = () => {
  const index = new Date().getMonth()
  return months[index]?.key || 'jan'
}

// Convert short key to full month name for API
const keyToMonthName = (key) => {
  const mapping = {
    jan: 'january',
    feb: 'february',
    mar: 'march',
    apr: 'april',
    may: 'may',
    jun: 'june',
    jul: 'july',
    aug: 'august',
    sep: 'september',
    oct: 'october',
    nov: 'november',
    dec: 'december'
  }
  return mapping[key] || 'january'
}

const buildDriveCandidates = (image) => {
  const candidates = [
    image.imageUrl,
    image.thumbnailUrl,
    image.userContentUrl,
    image.lh3Url,
    image.downloadUrl
  ].filter(Boolean)

  return candidates
}

const GospelToonsTamilNew = () => {
  const [activeMonth, setActiveMonth] = useState(getCurrentMonthKey)
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(true)
  const [monthLoading, setMonthLoading] = useState(false)
  const [lightboxData, setLightboxData] = useState({ 
    isOpen: false, 
    src: '', 
    caption: '', 
    images: [], 
    currentIndex: 0 
  })

  const loadMonth = useCallback((monthKey) => {
    setMonthLoading(true)
    setActiveMonth(monthKey)

    const callbackName = `gtTamilNewCallback_${Date.now()}_${Math.random().toString(36).slice(2)}`
    const month = months.find(m => m.key === monthKey)
    const monthNameTamil = month?.tamil || 'ஜனவரி'
    const apiMonthName = keyToMonthName(monthKey)

    const script = document.createElement('script')
    const separator = GOOGLE_DRIVE_API_URL.includes('?') ? '&' : '?'
    let timeoutId = null
    let settled = false

    const cleanup = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }

      if (script.parentNode) {
        script.parentNode.removeChild(script)
      }

      window.setTimeout(() => {
        try {
          delete window[callbackName]
        } catch {
          window[callbackName] = undefined
        }
      }, 1000)
    }

    window[callbackName] = (data) => {
      if (settled) return
      settled = true

      console.log('GT Tamil New raw response:', data)

      if (
        data &&
        !Array.isArray(data) &&
        data.status === 'error'
      ) {
        console.error('GT Tamil New API error:', data.message)
        setImages([])
        setMonthLoading(false)
        setLoading(false)
        cleanup()
        return
      }

      const receivedImages = Array.isArray(data)
        ? data
        : Array.isArray(data?.images)
          ? data.images
          : []

      console.log('[GT Tamil New] received count:', receivedImages.length)
      console.log('[GT Tamil New] first raw image:', receivedImages[0])

      const processedImages = receivedImages.map((image, index) => {
        const candidates = buildDriveCandidates(image)

        return {
          id:
            image.id ||
            `${image.name}-${index}`,

          name:
            image.name ||
            `Gospeltoon ${index + 1}`,

          alt:
            image.name ||
            `Gospeltoon ${index + 1}`,

          description:
            image.name ||
            'Catholic Gospeltoon',

          date: `${monthNameTamil}, 2026`,

          src: candidates[0] || '',

          candidates,

          loaded: false,

          failed: candidates.length === 0,

          successfulSrc: ''
        }
      })

      setImages(processedImages)
      setMonthLoading(false)
      setLoading(false)

      cleanup()
    }

    script.src =
      `${GOOGLE_DRIVE_API_URL}` +
      `${separator}type=gtTamilNew` +
      `&month=${encodeURIComponent(apiMonthName)}` +
      `&callback=${encodeURIComponent(callbackName)}` +
      `&t=${Date.now()}`

    script.async = true

    script.onerror = () => {
      if (settled) return
      settled = true

      console.error('GT Tamil New API request failed')
      setImages([])
      setMonthLoading(false)
      setLoading(false)
      cleanup()
    }

    timeoutId = window.setTimeout(() => {
      if (settled) return
      settled = true

      console.error('GT Tamil New API request timed out')
      setImages([])
      setMonthLoading(false)
      setLoading(false)
      cleanup()
    }, 15000)

    document.body.appendChild(script)

    return () => {
      cleanup()
    }
  }, [])

  useEffect(() => {
    setLoading(false)
    loadMonth(getCurrentMonthKey())
  }, [loadMonth])

  const openLightbox = (src, caption, images = [], currentIndex = 0) => {
    setLightboxData({ isOpen: true, src, caption, images, currentIndex })
  }

  const closeLightbox = () => {
    setLightboxData({ isOpen: false, src: '', caption: '', images: [], currentIndex: 0 })
  }

  const navigateLightbox = (direction) => {
    const { images, currentIndex } = lightboxData
    if (images.length === 0) return

    let newIndex = currentIndex
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % images.length
    } else if (direction === 'prev') {
      newIndex = (currentIndex - 1 + images.length) % images.length
    }

    const newImage = images[newIndex]
    setLightboxData({
      ...lightboxData,
      src: newImage.successfulSrc || newImage.src,
      caption: `${newImage.name} - ${newImage.date}`,
      currentIndex: newIndex
    })
  }

  const monthName = months.find(m => m.key === activeMonth)?.name || 'January'
  const monthNameTamil = months.find(m => m.key === activeMonth)?.tamil || 'ஜனவரி'
  const pageTitle = `கத்தோலிக்க கோஸ்பெல்டூன்ஸ் தமிழ் புதியது - ${monthNameTamil} 2026 | Sathish Paul SDB | Free Downloads`
  const pageDescription = `${monthNameTamil} 2026க்கான இலவச கத்தோலிக்க கோஸ்பெல்டூன்ஸ் தமிழில். சமீபத்திய வேதவசன விளக்கங்கள், கிறிஸ்தவ கலை, மற்றும் நம்பிக்கை அடிப்படையிலான கதை சொல்லும் காட்சிகள். Sathish Paul SDB இன் உயர்தர கோஸ்பெல் கார்டூன்களை பதிவிறக்கவும்.`
  
  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    "name": `Gospeltoons Tamil New - ${monthName} 2026`,
    "description": `Latest Catholic Gospeltoons in Tamil for ${monthName} 2026 with Bible verses and Christian content`,
    "url": typeof window !== 'undefined' ? window.location.href : 'https://www.sathishpaul.net/gospeltoons-tamil-new',
    "inLanguage": "ta",
    "creator": {
      "@type": "Person",
      "name": "Sathish Paul SDB"
    },
    "image": images.length > 0 ? images.map(img => ({
      "@type": "ImageObject",
      "url": img.src,
      "name": img.name || img.alt,
      "description": img.description || img.alt,
      "datePublished": img.date
    })) : []
  }

  return (
    <div className="gospeltoons-page">
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords={`கோஸ்பெல்டூன்ஸ் தமிழ் புதியது, கத்தோலிக்க கோஸ்பெல்டூன்ஸ் 2026, கிறிஸ்தவ கலை தமிழ், வேதவசன கலை, கோஸ்பெல் கார்டூன்கள் தமிழ், ${monthNameTamil} கோஸ்பெல்டூன்ஸ் 2026, கத்தோலிக்க கலை தமிழ், Sathish Paul SDB, Gospeltoons Tamil New, Catholic Gospeltoons Tamil 2026`}
        image="https://www.sathishpaul.net/images/GTTamil.jpeg"
        schema={pageSchema}
        hreflang={[
          { lang: 'en', url: 'https://www.sathishpaul.net/gospeltoons-eng-new' },
          { lang: 'ta', url: 'https://www.sathishpaul.net/gospeltoons-tamil-new' }
        ]}
      />
      <ImageSEO 
        images={images} 
        pageUrl={typeof window !== 'undefined' ? window.location.href : ''}
        pageTitle={`Gospeltoons Tamil New - ${monthName} 2026`}
      />
      <div className="page-content">
        <MonthSidebar 
          months={months}
          activeMonth={activeMonth}
          onMonthClick={loadMonth}
          language="tamil"
        />

        <div className="content-area">
          {monthLoading && (
            <div className="month-loader">
              <ThreeDots
                height={60}
                width={60}
                color="#ffa72c"
                secondaryColor="#ffa72c"
                visible={true}
                ariaLabel="dots-loading"
              />
            </div>
          )}

          <div className="gallery-grid">
            {loading ? (
              <div className="gospel-loader">
                <ThreeDots
                  height={60}
                  width={60}
                  color="#ffa72c"
                  secondaryColor="#ffa72c"
                  visible={true}
                  ariaLabel="oval-loading"
                />
              </div>
            ) : images.length === 0 ? (
              <div className="no-data-message">No images available for this month</div>
            ) : (
              images.map((image, index) => (
                <div
                  key={image.id || `${image.name}-${index}`}
                  className="gallery-card"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  onClick={() => {
                    if (image.failed) return
                    openLightbox(
                      image.successfulSrc || image.src,
                      image.name,
                      images,
                      index
                    )
                  }}
                >
                  {image.failed && (
                    <div className="image-load-failed">
                      Image unavailable
                    </div>
                  )}
                  {!image.loaded && !image.failed && (
                    <div className="image-skeleton" />
                  )}
                  {!image.failed && (
                    <img
                      src={image.src}
                      alt={image.alt}
                      loading="lazy"
                      data-source-index="0"
                      onLoad={(event) => {
                        const successfulUrl =
                          event.currentTarget.currentSrc ||
                          event.currentTarget.src

                        setImages(current =>
                          current.map(item =>
                            item.id === image.id &&
                            (!item.loaded ||
                              item.successfulSrc !== successfulUrl)
                              ? {
                                  ...item,
                                  loaded: true,
                                  failed: false,
                                  src: successfulUrl,
                                  successfulSrc: successfulUrl
                                }
                              : item
                          )
                        )
                      }}
                      onError={(event) => {
                        const img = event.currentTarget

                        const candidates = Array.isArray(image.candidates)
                          ? image.candidates
                          : [image.src].filter(Boolean)

                        const currentIndex =
                          Number(img.dataset.sourceIndex || 0)

                        let nextIndex = currentIndex + 1
                        let nextUrl = candidates[nextIndex]
                        const failedUrl = img.currentSrc || img.src

                        while (nextUrl && nextUrl === failedUrl) {
                          nextIndex += 1
                          nextUrl = candidates[nextIndex]
                        }

                        if (nextUrl) {
                          img.dataset.sourceIndex = String(nextIndex)
                          img.src = nextUrl
                          return
                        }

                        img.onerror = null

                        setImages(current =>
                          current.map(item =>
                            item.id === image.id
                              ? {
                                  ...item,
                                  loaded: false,
                                  failed: true
                                }
                              : item
                          )
                        )
                      }}
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <Lightbox 
        isOpen={lightboxData.isOpen}
        src={lightboxData.src}
        caption={lightboxData.caption}
        onClose={closeLightbox}
        onNavigate={navigateLightbox}
        hasMultipleImages={lightboxData.images.length > 1}
        currentIndex={lightboxData.currentIndex}
        totalImages={lightboxData.images.length}
      />
    </div>
  )
}

export default GospelToonsTamilNew

